
import axios from 'axios'
import { ElMessage } from 'element-plus'
import type { UploadFile, UploadUserFile } from 'element-plus'
import { computed, defineComponent, inject, ref } from 'vue'
import { AuthPlugin } from '@/auth'
import {
  ERR_VIDEO_MAX_BYTES,
  ERR_VIDEO_MAX_FILES,
  isMp4File,
} from '@/utils/err-video-upload'

type ItemStatus = 'ready' | 'uploading' | 'succeeded' | 'failed'

interface UploadItem {
  uid: number
  name: string
  file: File
  progress: number
  status: ItemStatus
  error?: string
  trapeziusThickness: string
  trapeziusSubcutaneousFatThickness: string
  subcutaneousFatThickness: string
  latissimusThickness: string
}

/* upload-url API は snake_case のため camelcase をこのブロックのみ無効化 */
/* eslint-disable camelcase */
interface UploadUrlResponse {
  upload_url: string
  json_upload_url: string
  measurement_json: string
  content_type: string
}
/* eslint-enable camelcase */

const toCm = (raw: string): number => {
  if (raw === '' || raw == null) {
    return 0
  }
  const value = Number(raw)
  return Number.isFinite(value) && value >= 0 ? value : 0
}

const putBody = (
  url: string,
  body: File | string,
  contentType: string,
  onProgress: (percent: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100)
        resolve()
        return
      }
      reject(new Error(`アップロードに失敗しました (${xhr.status})`))
    }
    xhr.onerror = () => reject(new Error('アップロードに失敗しました'))
    xhr.send(body)
  })
}

export default defineComponent({
  name: 'FailedVideos',
  setup() {
    const backendUrl: string | undefined = inject('BackendUrl')
    if (!backendUrl) {
      throw new Error('BackendUrl is not provided')
    }
    const auth: AuthPlugin | undefined = inject('Auth')
    if (!auth) {
      throw new Error('Auth plugin is not provided')
    }

    const maxFiles = ERR_VIDEO_MAX_FILES
    const comment = ref('')
    const fileList = ref<UploadUserFile[]>([])
    const items = ref<UploadItem[]>([])
    const isUploading = ref(false)

    // 送信元はログインユーザーでバックエンドが確定するため、区分選択は不要。
    const canSubmit = computed(
      () => items.value.length > 0 && !isUploading.value,
    )

    const progressStatus = (status: ItemStatus) => {
      if (status === 'succeeded') {
        return 'success'
      }
      if (status === 'failed') {
        return 'exception'
      }
      return undefined
    }

    const syncFiles = (files: UploadFile[]) => {
      const previousByUid = new Map(items.value.map((item) => [item.uid, item]))
      const next: UploadItem[] = []
      const accepted: UploadUserFile[] = []
      for (const entry of files) {
        if (next.length >= maxFiles) {
          ElMessage.warning(`一度にアップロードできるファイル数は${maxFiles}件までです`)
          break
        }
        const raw = entry.raw
        if (!raw) {
          continue
        }
        if (!isMp4File(raw)) {
          ElMessage.warning(`${entry.name} は MP4 ではありません`)
          continue
        }
        if (raw.size > ERR_VIDEO_MAX_BYTES) {
          ElMessage.warning(`${entry.name} は 500MB を超えています`)
          continue
        }
        const previous = previousByUid.get(entry.uid)
        next.push({
          uid: entry.uid,
          name: raw.name,
          file: raw,
          progress: previous?.progress ?? 0,
          status: previous?.status ?? 'ready',
          error: previous?.error,
          trapeziusThickness: previous?.trapeziusThickness ?? '',
          trapeziusSubcutaneousFatThickness:
            previous?.trapeziusSubcutaneousFatThickness ?? '',
          subcutaneousFatThickness: previous?.subcutaneousFatThickness ?? '',
          latissimusThickness: previous?.latissimusThickness ?? '',
        })
        accepted.push(entry)
      }
      items.value = next
      fileList.value = accepted
    }

    const handleFileChange = (_file: UploadFile, files: UploadFile[]) => {
      syncFiles(files)
    }

    const handleFileRemove = (_file: UploadFile, files: UploadFile[]) => {
      syncFiles(files)
    }

    const handleExceed = () => {
      ElMessage.warning(`一度にアップロードできるファイル数は${maxFiles}件までです`)
    }

    const buildHeaders = async () => {
      const idToken = (await auth.getIdTokenClaims({}))?.__raw
      const impersonationToken = localStorage.getItem('impersonationToken')
      const headers: Record<string, string> = {
        Authorization: `Bearer ${idToken}`,
        'X-AIMEAT-Upload-Origin': window.location.origin,
      }
      if (impersonationToken) {
        headers['X-AIMEAT-Impersonation-Token'] = impersonationToken
      }
      return headers
    }

    /* eslint-disable camelcase */
    const uploadOne = async (item: UploadItem) => {
      item.status = 'uploading'
      item.progress = 0
      item.error = undefined
      const response = await axios.post<UploadUrlResponse>(
        `${backendUrl}/v1/failed-videos/upload-url`,
        {
          filename: item.file.name,
          size: item.file.size,
          comment: comment.value || undefined,
          trapezius_thickness: toCm(item.trapeziusThickness),
          trapezius_subcutaneous_fat_thickness: toCm(
            item.trapeziusSubcutaneousFatThickness,
          ),
          subcutaneous_fat_thickness: toCm(item.subcutaneousFatThickness),
          latissimus_thickness: toCm(item.latissimusThickness),
        },
        { headers: await buildHeaders() },
      )
      await putBody(
        response.data.upload_url,
        item.file,
        response.data.content_type,
        (percent) => {
          item.progress = Math.min(90, percent)
        },
      )
      await putBody(
        response.data.json_upload_url,
        response.data.measurement_json,
        'application/json',
        () => undefined,
      )
      item.status = 'succeeded'
      item.progress = 100
    }
    /* eslint-enable camelcase */

    const submit = async () => {
      if (!canSubmit.value) {
        return
      }
      isUploading.value = true
      let succeeded = 0
      try {
        for (const item of items.value) {
          if (item.status === 'succeeded') {
            succeeded += 1
            continue
          }
          try {
            await uploadOne(item)
            succeeded += 1
          } catch (error) {
            item.status = 'failed'
            const axiosError = error as {
              response?: { data?: { error?: string } }
              message?: string
            }
            item.error =
              axiosError.response?.data?.error ||
              axiosError.message ||
              '送信に失敗しました'
          }
        }
        if (succeeded === items.value.length) {
          ElMessage.success('送信しました。MIJL で確認します。')
        } else if (succeeded > 0) {
          ElMessage.warning('一部のファイルの送信に失敗しました')
        } else {
          ElMessage.error('送信に失敗しました')
        }
      } finally {
        isUploading.value = false
      }
    }

    return {
      maxFiles,
      comment,
      fileList,
      items,
      isUploading,
      canSubmit,
      progressStatus,
      handleFileChange,
      handleFileRemove,
      handleExceed,
      submit,
    }
  },
})
